import { isolateHistory } from "@codemirror/commands";
import { markdown } from "@codemirror/lang-markdown";
import {
    Compartment,
    EditorState,
    RangeSetBuilder,
    StateEffect,
    StateField,
    Transaction,
    type Extension,
    type StateEffect as StateEffectType,
} from "@codemirror/state";
import { Decoration, EditorView, keymap, type DecorationSet } from "@codemirror/view";
import { indentationMarkers } from "@replit/codemirror-indentation-markers";
import { vim, Vim, getCM } from "@replit/codemirror-vim";
import { vscodeKeymap } from "@replit/codemirror-vscode-keymap";
import { minimalSetup } from "codemirror";
import { useEffect, useMemo, useRef, useState } from "react";
import { registerNoteLinkRepairHandler, repairedMarkdown } from "@/lib/note-link-repair";
import { supportsAI } from "@/lib/platform";
import type { WritingEditorAdapter } from "../writing-assistant/types";
import {
    editorActionsGlobalRef,
    editorGlobalRef,
    editorSearchGlobalRef,
    registerEditorActivation,
    type EditorSearchHandle,
    setEditorActionsGlobalRef,
    setEditorSearchGlobalRef,
} from "../editor-global-ref";
import { markdownCompletion } from "../extensions/markdown-completion";
import { addMarkdownFrontmatter, hasMarkdownFrontmatter } from "../frontmatter";
import { applyCodeMirrorVimConfiguration } from "../vim/apply-codemirror-configuration";
import { useVimConfiguration } from "../vim/use-vim-configuration";
import { createMarkdownWritingAssistant } from "../writing-assistant/markdown-adapter";
import { WritingAssistant } from "../writing-assistant/writing-assistant";
import { appDarkCodeMirrorTheme, appLightCodeMirrorTheme } from "./code-block/editor-themes.ts";

const MAIN_SCROLL_CONTAINER_ID = "main-scroll-container";
const CURSOR_SCROLL_MARGIN_PX = 96;
const setMarkdownSearchDecorations = StateEffect.define<DecorationSet>();

function createMarkdownEditorContentAttributes(
    readonly: boolean,
    writingAssistantEnabled: boolean,
): Record<string, string> {
    return {
        role: "textbox",
        "aria-label": "Markdown note editor",
        "aria-multiline": "true",
        "aria-readonly": String(readonly),
        spellcheck: writingAssistantEnabled && !readonly ? "true" : "false",
    };
}

const markdownSearchDecorations = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(decorations, transaction) {
        const mappedDecorations = decorations.map(transaction.changes);
        for (const effect of transaction.effects) {
            if (effect.is(setMarkdownSearchDecorations)) return effect.value;
        }
        return mappedDecorations;
    },
    provide: (field) => EditorView.decorations.from(field),
});

type MarkdownMatch = { from: number; to: number };

function findMarkdownMatches(content: string, searchTerm: string, caseSensitive: boolean): MarkdownMatch[] {
    if (!searchTerm) return [];
    const source = caseSensitive ? content : content.toLocaleLowerCase();
    const needle = caseSensitive ? searchTerm : searchTerm.toLocaleLowerCase();
    const matches: MarkdownMatch[] = [];
    let from = 0;

    while (from <= source.length - needle.length) {
        const index = source.indexOf(needle, from);
        if (index === -1) break;
        matches.push({ from: index, to: index + needle.length });
        from = index + Math.max(needle.length, 1);
    }

    return matches;
}

function buildMarkdownSearchDecorations(matches: MarkdownMatch[], resultIndex: number): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    for (const [index, match] of matches.entries()) {
        builder.add(
            match.from,
            match.to,
            Decoration.mark({ class: index === resultIndex ? "cm-searchMatch-current" : "cm-searchMatch" }),
        );
    }
    return builder.finish();
}

export function createMarkdownSearchController(
    view: EditorView,
): EditorSearchHandle & { dispose: () => void; refreshDocument: () => void } {
    let searchTerm = "";
    let replaceTerm = "";
    let caseSensitive = false;
    let matches: MarkdownMatch[] = [];
    let resultIndex = 0;
    const listeners = new Set<() => void>();

    const notify = (): void => {
        for (const listener of listeners) listener();
    };

    const updateView = (selectCurrent: boolean): void => {
        const current = matches[resultIndex];
        const effects: StateEffectType<unknown>[] = [
            setMarkdownSearchDecorations.of(buildMarkdownSearchDecorations(matches, resultIndex)),
        ];
        if (selectCurrent && current) {
            effects.push(EditorView.scrollIntoView(current.from, { y: "center" }));
        }
        view.dispatch({
            effects,
            selection: selectCurrent && current ? { anchor: current.from, head: current.to } : undefined,
        });
        notify();
    };

    const refresh = (selectCurrent: boolean): void => {
        matches = findMarkdownMatches(view.state.doc.toString(), searchTerm, caseSensitive);
        resultIndex = Math.min(resultIndex, Math.max(matches.length - 1, 0));
        updateView(selectCurrent);
    };

    const controller: EditorSearchHandle & { dispose: () => void; refreshDocument: () => void } = {
        getState: () => ({
            searchTerm,
            replaceTerm,
            resultsCount: matches.length,
            resultIndex,
            caseSensitive,
        }),
        getContent: () => view.state.doc.toString(),
        setSearchTerm: (nextSearchTerm) => {
            searchTerm = nextSearchTerm;
            resultIndex = 0;
            refresh(true);
        },
        setReplaceTerm: (nextReplaceTerm) => {
            replaceTerm = nextReplaceTerm;
            notify();
        },
        setCaseSensitive: (nextCaseSensitive) => {
            caseSensitive = nextCaseSensitive;
            resultIndex = 0;
            refresh(true);
        },
        nextSearchResult: () => {
            if (matches.length === 0) return;
            resultIndex = (resultIndex + 1) % matches.length;
            updateView(true);
        },
        previousSearchResult: () => {
            if (matches.length === 0) return;
            resultIndex = (resultIndex - 1 + matches.length) % matches.length;
            updateView(true);
        },
        replace: (nextReplaceTerm) => {
            const current = matches[resultIndex];
            if (!current) return;
            view.dispatch({
                changes: { from: current.from, to: current.to, insert: nextReplaceTerm },
            });
            refresh(true);
        },
        replaceAll: (nextReplaceTerm) => {
            if (!searchTerm || matches.length === 0) return;
            view.dispatch({
                changes: matches.map((match) => ({ from: match.from, to: match.to, insert: nextReplaceTerm })),
            });
            resultIndex = 0;
            refresh(false);
        },
        focus: () => view.focus(),
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        dispose: () => {
            listeners.clear();
        },
        refreshDocument: () => refresh(false),
    };

    return controller;
}

type RawMarkdownEditorProps = {
    noteId?: string;
    repairNoteId?: string;
    value: string;
    onChange: (value: string) => void;
    readonly?: boolean;
    theme: string;
    fontSize: number;
    vimMode?: boolean;
    active?: boolean;
    writingAssistantEnabled: boolean;
};

function getEditorTopInScrollContainer(view: EditorView, scrollContainer: HTMLElement): number {
    const containerRect = scrollContainer.getBoundingClientRect();
    const editorRect = view.scrollDOM.getBoundingClientRect();
    return scrollContainer.scrollTop + editorRect.top - containerRect.top;
}

function scrollMainContainerToCursor(view: EditorView): void {
    const scrollContainer = document.getElementById(MAIN_SCROLL_CONTAINER_ID);
    if (!scrollContainer) return;

    const cursorPosition = view.state.selection.main.head;
    const lineBlock = view.lineBlockAt(cursorPosition);
    const editorTop = getEditorTopInScrollContainer(view, scrollContainer);
    const cursorTop = editorTop + lineBlock.top - view.scrollDOM.scrollTop;
    const cursorBottom = editorTop + lineBlock.bottom - view.scrollDOM.scrollTop;
    const visibleTop = scrollContainer.scrollTop + CURSOR_SCROLL_MARGIN_PX;
    const visibleBottom = scrollContainer.scrollTop + scrollContainer.clientHeight - CURSOR_SCROLL_MARGIN_PX;

    if (cursorBottom > visibleBottom) {
        scrollContainer.scrollTop += cursorBottom - visibleBottom;
        return;
    }

    if (cursorTop < visibleTop) {
        scrollContainer.scrollTop += cursorTop - visibleTop;
    }
}

function createKeymapExtensions(vimMode: boolean): Extension[] {
    if (!vimMode) return [keymap.of(vscodeKeymap)];

    const vimModifierKeymap = EditorView.domEventHandlers({
        keydown: (event, view) => {
            if (event.key.toLowerCase() !== "w" || !event.ctrlKey || event.altKey || event.shiftKey) return false;

            event.preventDefault();
            event.stopPropagation();
            const cm = getCM(view);
            if (cm) Vim.handleKey(cm, "<C-w>", "user");
            return true;
        },
    });

    return [vim(), vimModifierKeymap, keymap.of(vscodeKeymap)];
}

function createRawMarkdownEditorTheme(fontSize: number) {
    return EditorView.theme({
        "&": {
            minHeight: "60vh",
            width: "100%",
            background: "transparent",
            color: "var(--var-color-foreground)",
            fontSize: `${fontSize}px`,
        },
        ".cm-content": {
            minHeight: "60vh",
            padding: "0 0 6rem",
            caretColor: "var(--var-color-foreground)",
            fontFamily:
                "'JetBrains Mono', 'FiraCode Nerd Font', 'Symbols Nerd Font', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            lineHeight: "1.7",
        },
        ".cm-line": {
            padding: "0",
        },
        ".cm-searchMatch": {
            backgroundColor: "color-mix(in srgb, var(--var-color-primary) 20%, transparent)",
            borderRadius: "2px",
        },
        ".cm-searchMatch-current": {
            backgroundColor: "color-mix(in srgb, var(--var-color-primary) 45%, transparent)",
            borderRadius: "2px",
            boxShadow: "0 0 0 1px color-mix(in srgb, var(--var-color-primary) 70%, transparent)",
        },
        ".cm-cursor, .cm-dropCursor": {
            borderLeftColor: "var(--var-color-foreground)",
        },
        ".cm-focused": {
            outline: "none",
        },
        ".cm-scroller": {
            overflow: "visible",
            fontFamily:
                "'JetBrains Mono', 'FiraCode Nerd Font', 'Symbols Nerd Font', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        },
        ".cm-gutters": {
            backgroundColor: "transparent",
            border: "none",
            color: "var(--var-color-muted-foreground)",
        },
        ".cm-activeLine, .cm-activeLineGutter": {
            backgroundColor: "color-mix(in srgb, var(--var-color-muted) 25%, transparent)",
        },
        ".cm-vim-panel": {
            borderTop: "1px solid var(--var-color-border)",
            backgroundColor: "var(--var-color-background)",
            color: "var(--var-color-foreground)",
            fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        },
        ".cm-vim-panel input": {
            backgroundColor: "transparent",
            color: "var(--var-color-foreground)",
        },
        ".cm-fat-cursor": {
            backgroundColor: "var(--var-color-foreground)",
            outline: "solid 1px var(--var-color-foreground)",
        },
        ".cm-fat-cursor .cm-cursor-primary": {
            borderLeftColor: "transparent",
        },
        ".cm-content.cm-fat-cursor": {
            caretColor: "transparent",
        },
        ".cm-indent-marker": {
            borderLeftColor: "color-mix(in srgb, var(--var-color-border) 35%, transparent)",
        },
        ".cm-indent-marker-active": {
            borderLeftColor: "color-mix(in srgb, var(--var-color-primary) 55%, transparent)",
        },
    });
}

export function RawMarkdownEditor({
    noteId,
    repairNoteId,
    value,
    onChange,
    readonly = false,
    theme,
    fontSize,
    vimMode = false,
    active = true,
    writingAssistantEnabled,
}: RawMarkdownEditorProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    const repairNoteIdRef = useRef(repairNoteId ?? noteId);
    useEffect(() => {
        repairNoteIdRef.current = repairNoteId ?? noteId;
    }, [repairNoteId, noteId]);
    const onChangeRef = useRef(onChange);
    const valueRef = useRef(value);
    const initialThemeRef = useRef(theme);
    const initialFontSizeRef = useRef(fontSize);
    const initialReadonlyRef = useRef(readonly);
    const initialVimModeRef = useRef(vimMode);
    const {
        configuration: vimConfiguration,
        error: vimConfigurationError,
        loading: vimConfigurationLoading,
    } = useVimConfiguration();
    const initialActiveRef = useRef(active);
    const isSyncingExternalValueRef = useRef(false);
    const keymapCompartmentRef = useRef(new Compartment());
    const visualThemeCompartmentRef = useRef(new Compartment());
    const editorThemeCompartmentRef = useRef(new Compartment());
    const editableCompartmentRef = useRef(new Compartment());
    const readOnlyCompartmentRef = useRef(new Compartment());
    const contentAttributesCompartmentRef = useRef(new Compartment());
    const activateRef = useRef<() => void>(() => { });
    const [writingAssistantIntegration] = useState(() => createMarkdownWritingAssistant());
    const [writingAssistantAdapter, setWritingAssistantAdapter] = useState<WritingEditorAdapter | null>(null);
    const writingAssistantSelectionContext = useMemo(() => {
        const view = writingAssistantAdapter ? viewRef.current : null;
        if (!view) return null;
        return {
            target: view.dom,
            readSelection: () => {
                const { from, to } = view.state.selection.main;
                if (from < to) return { from, to };

                const selection = view.dom.ownerDocument.getSelection();
                if (
                    selection &&
                    !selection.isCollapsed &&
                    selection.anchorNode &&
                    selection.focusNode &&
                    view.dom.contains(selection.anchorNode) &&
                    view.dom.contains(selection.focusNode)
                ) {
                    try {
                        const anchor = view.posAtDOM(selection.anchorNode, selection.anchorOffset);
                        const head = view.posAtDOM(selection.focusNode, selection.focusOffset);
                        const from = Math.min(anchor, head);
                        const to = Math.max(anchor, head);
                        if (from < to) return { from, to };
                    } catch {
                        return null;
                    }
                }
                return null;
            },
            readAnchor: (selection?: { from: number; to: number }) => {
                const currentSelection = selection ?? view.state.selection.main;
                try {
                    const lineStart = view.state.doc.lineAt(currentSelection.from).from;
                    const start = view.coordsAtPos(lineStart);
                    if (!start) return null;
                    const end = view.coordsAtPos(currentSelection.to) ?? start;
                    return { left: start.left, top: start.top, bottom: end.bottom };
                } catch {
                    return null;
                }
            },
        };
    }, [writingAssistantAdapter]);
    const writingAssistantAdapterRef = useRef<WritingEditorAdapter | null>(null);
    const initialWritingAssistantEnabledRef = useRef(writingAssistantEnabled);
    const writingAssistantEnabledRef = useRef(writingAssistantEnabled);
    const writingAssistantCompartmentRef = useRef(new Compartment());

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    useEffect(() => {
        if (!containerRef.current) return;

        let cursorScrollFrame: number | null = null;
        let searchController: (EditorSearchHandle & { dispose: () => void; refreshDocument: () => void }) | null = null;
        const scheduleCursorScroll = (editorView: EditorView): void => {
            if (cursorScrollFrame !== null) return;
            cursorScrollFrame = requestAnimationFrame(() => {
                cursorScrollFrame = null;
                scrollMainContainerToCursor(editorView);
            });
        };

        const isDark = initialThemeRef.current !== "light";
        const initialReadonly = initialReadonlyRef.current;
        const view = new EditorView({
            parent: containerRef.current,
            state: EditorState.create({
                doc: valueRef.current,
                extensions: [
                    markdownCompletion(noteId),
                    keymapCompartmentRef.current.of(createKeymapExtensions(initialVimModeRef.current)),
                    minimalSetup,
                    markdown({
                        addKeymap: true,
                        completeHTMLTags: true,
                        pasteURLAsLink: true,
                    }),
                    markdownSearchDecorations,
                    writingAssistantCompartmentRef.current.of(
                        initialWritingAssistantEnabledRef.current ? writingAssistantIntegration.extension : [],
                    ),
                    EditorView.lineWrapping,
                    indentationMarkers({
                        activeThickness: 2,
                        hideFirstIndent: true,
                        markerType: "codeOnly",
                        colors: {
                            light: "color-mix(in srgb, var(--var-color-border) 35%, transparent)",
                            dark: "color-mix(in srgb, var(--var-color-border) 35%, transparent)",
                            activeLight: "color-mix(in srgb, var(--var-color-primary) 55%, transparent)",
                            activeDark: "color-mix(in srgb, var(--var-color-primary) 55%, transparent)",
                        },
                    }),
                    visualThemeCompartmentRef.current.of(isDark ? appDarkCodeMirrorTheme() : appLightCodeMirrorTheme()),
                    editorThemeCompartmentRef.current.of(createRawMarkdownEditorTheme(initialFontSizeRef.current)),
                    editableCompartmentRef.current.of(EditorView.editable.of(!initialReadonly)),
                    readOnlyCompartmentRef.current.of(EditorState.readOnly.of(initialReadonly)),
                    contentAttributesCompartmentRef.current.of(
                        EditorView.contentAttributes.of(
                            createMarkdownEditorContentAttributes(
                                initialReadonly,
                                initialWritingAssistantEnabledRef.current,
                            ),
                        ),
                    ),
                    EditorView.updateListener.of((update) => {
                        if (update.docChanged || update.selectionSet) {
                            scheduleCursorScroll(update.view);
                        }

                        if (!update.docChanged) return;
                        searchController?.refreshDocument();
                        if (isSyncingExternalValueRef.current) {
                            isSyncingExternalValueRef.current = false;
                            return;
                        }
                        const nextValue = update.state.doc.toString();
                        valueRef.current = nextValue;
                        onChangeRef.current(nextValue);
                    }),
                ],
            }),
        });

        viewRef.current = view;
        const unregisterRepair = registerNoteLinkRepairHandler({
            noteId: () => repairNoteIdRef.current,
            getContent: () => view.state.doc.toString(),
            isEditable: () => !view.state.readOnly,
            apply: (request) => {
                if (view.state.readOnly) return "unavailable";
                if (repairedMarkdown(view.state.doc.toString(), request) === null) return "conflict";
                view.dispatch({
                    changes: { from: request.from, to: request.to, insert: request.replacement },
                    annotations: [isolateHistory.of("full"), Transaction.userEvent.of("input.repair")],
                });
                return "applied";
            },
        });
        if (initialWritingAssistantEnabledRef.current) {
            const adapter = writingAssistantIntegration.createAdapter(view);
            writingAssistantAdapterRef.current = adapter;
            setWritingAssistantAdapter(adapter);
        }
        searchController = createMarkdownSearchController(view);
        const actionHandle = {
            addFrontmatter: () => {
                if (initialReadonly) {
                    view.focus();
                    return;
                }

                const currentContent = view.state.doc.toString();
                if (hasMarkdownFrontmatter(currentContent)) {
                    view.focus();
                    return;
                }

                const nextContent = addMarkdownFrontmatter(currentContent);
                const frontmatter = nextContent.slice(0, nextContent.length - currentContent.length);
                view.dispatch({ changes: { from: 0, to: 0, insert: frontmatter } });
                view.focus();
            },
        };
        const activate = () => {
            editorGlobalRef.current = null;
            setEditorActionsGlobalRef(actionHandle);
            setEditorSearchGlobalRef(searchController);
        };
        const activation = registerEditorActivation(activate);
        activateRef.current = activation.activate;
        view.dom.addEventListener("focus", activation.activate, true);
        if (initialActiveRef.current || (!editorActionsGlobalRef.current && !editorSearchGlobalRef.current))
            activation.activate();
        if (!initialReadonly && initialActiveRef.current) {
            requestAnimationFrame(() => view.focus());
        }

        return () => {
            activation.unregister();
            unregisterRepair();
            view.dom.removeEventListener("focus", activation.activate, true);
            if (cursorScrollFrame !== null) {
                cancelAnimationFrame(cursorScrollFrame);
            }
            if (editorActionsGlobalRef.current === actionHandle) {
                setEditorActionsGlobalRef(null);
            }
            if (editorSearchGlobalRef.current === searchController) {
                setEditorSearchGlobalRef(null);
            }
            if (activateRef.current === activation.activate) {
                activateRef.current = () => { };
            }
            writingAssistantAdapterRef.current?.dispose();
            writingAssistantAdapterRef.current = null;
            searchController?.dispose();
            view.destroy();
            viewRef.current = null;
        };
    }, []);

    useEffect(() => {
        const view = viewRef.current;
        if (!view || writingAssistantEnabledRef.current === writingAssistantEnabled) return;

        writingAssistantAdapterRef.current?.dispose();
        writingAssistantAdapterRef.current = null;
        view.dispatch({
            effects: [
                writingAssistantCompartmentRef.current.reconfigure(
                    writingAssistantEnabled ? writingAssistantIntegration.extension : [],
                ),
                contentAttributesCompartmentRef.current.reconfigure(
                    EditorView.contentAttributes.of(
                        createMarkdownEditorContentAttributes(readonly, writingAssistantEnabled),
                    ),
                ),
            ],
        });
        writingAssistantEnabledRef.current = writingAssistantEnabled;

        if (writingAssistantEnabled) {
            const adapter = writingAssistantIntegration.createAdapter(view);
            writingAssistantAdapterRef.current = adapter;
            setWritingAssistantAdapter(adapter);
        } else {
            setWritingAssistantAdapter(null);
        }
    }, [readonly, writingAssistantEnabled, writingAssistantIntegration]);
    useEffect(() => {
        if (active) activateRef.current();
    }, [active]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        const isDark = theme !== "light";
        view.dispatch({
            effects: [
                visualThemeCompartmentRef.current.reconfigure(
                    isDark ? appDarkCodeMirrorTheme() : appLightCodeMirrorTheme(),
                ),
                editorThemeCompartmentRef.current.reconfigure(createRawMarkdownEditorTheme(fontSize)),
                editableCompartmentRef.current.reconfigure(EditorView.editable.of(!readonly)),
                readOnlyCompartmentRef.current.reconfigure(EditorState.readOnly.of(readonly)),
                contentAttributesCompartmentRef.current.reconfigure(
                    EditorView.contentAttributes.of(
                        createMarkdownEditorContentAttributes(readonly, writingAssistantEnabled),
                    ),
                ),
            ],
        });
    }, [fontSize, readonly, theme, vimMode, writingAssistantEnabled]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view || vimConfigurationLoading) return;
        applyCodeMirrorVimConfiguration(vimConfiguration);
        view.dispatch({ effects: keymapCompartmentRef.current.reconfigure(createKeymapExtensions(vimMode)) });
        const cm = getCM(view);
        if (vimMode && cm && vimConfiguration.options.initialMode === "insert" && !cm.state.vim?.insertMode)
            Vim.handleKey(cm, "i", "api");
    }, [vimMode, vimConfiguration, vimConfigurationLoading]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        const currentValue = view.state.doc.toString();
        if (currentValue === value) return;
        isSyncingExternalValueRef.current = true;
        valueRef.current = value;
        view.dispatch({
            changes: { from: 0, to: currentValue.length, insert: value },
            annotations: Transaction.addToHistory.of(false),
        });
    }, [value]);

    return (
        <div className="w-full">
            {vimConfigurationError ? (
                <output role="alert" className="text-danger">
                    Vim configuration: {vimConfigurationError}
                </output>
            ) : null}
            {supportsAI() && writingAssistantEnabled && writingAssistantAdapter && (
                <WritingAssistant
                    adapter={writingAssistantAdapter}
                    selectionContext={writingAssistantSelectionContext}
                    disabled={readonly}
                />
            )}
            <div ref={containerRef} className="w-full print:block" data-raw-markdown-editor="true" />
        </div>
    );
}
